import React from "react";
import { StyleSheet, View } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Fonts } from "@/constants/ThemeColours";

export interface IComponentRefTagProps {
  name: string;
}

/**
 * Visual reference pill for cataloging and citing component variants across Designer workbenches.
 * Designed for high typographic legibility, crisp contrast, and clean separation between the
 * "REF" prefix and the component identifier.
 *
 * @param {IComponentRefTagProps} props - Tag properties.
 * @returns {React.JSX.Element} The rendered reference tag.
 */
export function ComponentRefTag({ name }: IComponentRefTagProps): React.JSX.Element {
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const textColor = useThemeColor({}, "text");

  // Parse "REF: <ComponentName>" or "REF <ComponentName>" into prefix + target
  const match = name.match(/^(REF:?\s*)(.*)$/i);
  const prefix = match ? match[1].trim() : null;
  const target = match ? match[2].trim() : name;

  return (
    <View style={[styles.componentRefBadge, { backgroundColor: badgeBg, borderColor }]}>
      {prefix ? (
        <ThemedText style={[styles.componentRefPrefix, { color: tintColor }]}>
          {prefix}
        </ThemedText>
      ) : null}
      <ThemedText style={[styles.componentRefText, { color: textColor }]}>
        {target}
      </ThemedText>
    </View>
  );
}

const styles = StyleSheet.create({
  componentRefBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    borderWidth: 1,
    alignSelf: "flex-start",
  },
  componentRefPrefix: {
    fontFamily: Fonts.sans,
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.6,
    textTransform: "uppercase",
  },
  componentRefText: {
    fontFamily: Fonts.mono,
    fontSize: 11.5,
    fontWeight: "600",
    letterSpacing: 0.3,
  },
});
